import { trialByPlan15535Variant } from '@/services/ab/experiments'
import { platform } from '../platform'

export interface PlatformTrialPolicy {
  plan_name: string
  trial_days: number | null
}

interface TrialPolicy {
  web: PlatformTrialPolicy
  ios: PlatformTrialPolicy
}

export interface TrialByPlanExperimentVariantConfig {
  name: string
  monthly: TrialPolicy
  annual: TrialPolicy
}

/**
 * Get the configuration for given experiment variant.
 *
 * Expects that we have experiment configuration in the local storage, raises
 * an error if we don't.
 *
 * @param variant: A, B, C, D
 * @returns experiment configuration for the variant.
 */
export function getTrialByPlanVariantConfig(
  variant: string,
): TrialByPlanExperimentVariantConfig {
  const variantConfig = Object.values(TRIAL_BY_PLAN_VARIANTS).find(
    (v) => v.name === variant,
  )

  if (!variantConfig) {
    throw new Error(`Unknown trial variant: ${variant}`)
  }
  return variantConfig
}

/**
 * Get the trial policy for the given variant and platform.
 * @param variant: A, B, C, D
 * @param platform: web, ios
 * @param planType: monthly, annual
 * @returns trial policy for the variant and platform.
 */
export async function getTrialPolicy(
  variant: string,
  planType: 'monthly' | 'annual',
): Promise<PlatformTrialPolicy> {
  const config = getTrialByPlanVariantConfig(variant)
  const isNativeIOSApp = await platform.isNativeIOSApp()
  const platformConfig = isNativeIOSApp ? 'ios' : 'web'
  return config[planType][platformConfig]
}

/**
 * Get iOS plan name for the variant assigned to user.
 * @returns plan name for App Store.
 */
export function trialByPlanIOSPlanName(
  isMonthly: boolean = true,
): string | null {
  // Default to 'A' Variant of the experiment
  const trialByPlan = trialByPlan15535Variant()
  if (!trialByPlan) {
    return null
  }
  const config = getTrialByPlanVariantConfig(trialByPlan)
  if (isMonthly) {
    return config.monthly.ios.plan_name
  } else {
    return config.annual.ios.plan_name
  }
}

/**
 * Returns true if the user has a treatment variant in the trial_by_plan_15535 experiment.
 */
export function trialByPlan15535Treatment(): boolean {
  const variant = trialByPlan15535Variant()

  return variant !== null && ['B', 'C', 'D'].includes(variant)
}

const TRIAL_BY_PLAN_VARIANTS: Record<
  string,
  TrialByPlanExperimentVariantConfig
> = {
  A: {
    name: 'A',
    monthly: {
      web: {
        trial_days: 5,
        plan_name: 'plan_monthly',
      },
      ios: {
        trial_days: 7,
        plan_name: 'shortform_monthly',
      },
    },
    annual: {
      web: {
        trial_days: 5,
        plan_name: 'plan_annual',
      },
      ios: {
        trial_days: 7,
        plan_name: 'shortform_annual',
      },
    },
  },
  B: {
    name: 'B',
    monthly: {
      web: {
        trial_days: null,
        plan_name: 'plan_monthly',
      },
      ios: {
        trial_days: null,
        plan_name: 'shortform_monthly_no_trial',
      },
    },
    annual: {
      web: {
        trial_days: 5,
        plan_name: 'plan_annual',
      },
      ios: {
        trial_days: 7,
        plan_name: 'shortform_annual',
      },
    },
  },
  C: {
    name: 'C',
    monthly: {
      web: {
        trial_days: null,
        plan_name: 'plan_monthly',
      },
      ios: {
        trial_days: null,
        plan_name: 'shortform_monthly_no_trial',
      },
    },
    annual: {
      web: {
        trial_days: 14,
        plan_name: 'plan_annual',
      },
      ios: {
        trial_days: 14,
        plan_name: 'shortform_annual_2_week_trial',
      },
    },
  },
  D: {
    name: 'D',
    monthly: {
      web: {
        trial_days: 5,
        plan_name: 'plan_monthly',
      },
      ios: {
        trial_days: 7,
        plan_name: 'shortform_monthly',
      },
    },
    annual: {
      web: {
        trial_days: 14,
        plan_name: 'plan_annual',
      },
      ios: {
        trial_days: 14,
        plan_name: 'shortform_annual_2_week_trial',
      },
    },
  },
}
